import { useEffect, useState } from 'react';
import { CalendarClock, CheckCircle2, Save } from 'lucide-react';
import ErrorBanner from './ErrorBanner.jsx';
import { calcomAPI } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';

// "Connect Cal.com" — an API-KEY form (mirrors the Zoom creds card, not the Google
// OAuth redirect). The AI checks availability + books the meeting live on a call.
// Connecting Cal.com REPLACES Google as this tenant's calendar backend (the AI
// books through one calendar seam). The API key is write-only — stored encrypted,
// never returned; the card only shows a "connected" state + the event-type id.
export default function CalComCard({ refreshKey }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [state, setState] = useState({ connected: false, eventTypeId: null, username: null, lengthMin: null, lengthOptions: [] });
  const [apiKey, setApiKey] = useState('');
  const [eventTypeId, setEventTypeId] = useState('');
  // THE PRIMARY FIELD. Cal.com does not surface the numeric event-type id anywhere
  // in its UI — not the profile page, not the event-type list — so requiring it
  // meant asking an operator to go read an API response. Brian looked and couldn't
  // find it, which made this card unusable. Core resolves the link → id server-side
  // (resolveEventTypeFromUrl), and a bad link 400s with a message naming the
  // available slugs, which is why that message is rendered verbatim below.
  const [bookingUrl, setBookingUrl] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notReady, setNotReady] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const s = await calcomAPI.status();
      setState({
        connected: !!s?.connected,
        eventTypeId: s?.eventTypeId || null,
        username: s?.username || null,
        lengthMin: s?.lengthMin ?? null,
        lengthOptions: s?.lengthOptions || []
      });
      if (s?.eventTypeId) setEventTypeId(String(s.eventTypeId));
      if (s?.username) setUsername(s.username);
    } catch (err) {
      if (err.status === 404) setNotReady(true); // backend not deployed yet
      else setError(err.body?.message || err.message || 'Failed to load Cal.com status.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setError('');
    if (!apiKey.trim()) { setError('Paste your Cal.com API key.'); return; }
    // Either is accepted server-side; the link is what an operator actually has.
    if (!bookingUrl.trim() && !eventTypeId.trim()) {
      setError('Paste your Cal.com booking link — the one you send people.');
      return;
    }
    if (eventTypeId.trim() && !/^\d+$/.test(eventTypeId.trim())) {
      setError('If you use the advanced field, the event type ID must be numeric.');
      return;
    }
    setSaving(true);
    try {
      const s = await calcomAPI.update({
        apiKey: apiKey.trim(),
        // Prefer the link. Sending eventTypeId SKIPS core's resolver, which is also
        // what discovers the event's length — so the id path books at the default
        // duration. Only send it when the operator deliberately opened Advanced.
        ...(bookingUrl.trim() ? { bookingUrl: bookingUrl.trim() } : {}),
        ...(eventTypeId.trim() ? { eventTypeId: eventTypeId.trim() } : {}),
        ...(username.trim() ? { username: username.trim() } : {})
      });
      setState({
        connected: !!s?.connected,
        eventTypeId: s?.eventTypeId || eventTypeId.trim(),
        username: s?.username || username.trim() || null,
        lengthMin: s?.lengthMin ?? null,
        lengthOptions: s?.lengthOptions || []
      });
      setApiKey(''); // never keep the key in the field after save
    } catch (err) {
      setError(err.body?.message || err.message || 'Failed to save Cal.com credentials.');
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!confirm('Disconnect Cal.com? The assistant will no longer check availability or book meetings.')) return;
    setError('');
    try {
      await calcomAPI.remove();
      setApiKey('');
      await load();
    } catch (err) {
      setError(err.body?.message || err.message || 'Disconnect failed.');
    }
  };

  return (
    <section className="card">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock size={18} className="text-alfred-gold" />
        <h2 className="text-xl text-stone-100">Cal.com</h2>
        {state.connected && (
          <span className="ml-2 badge bg-emerald-900/40 text-emerald-300 border border-emerald-800 text-[10px] inline-flex items-center gap-1">
            <CheckCircle2 size={10} /> Connected
          </span>
        )}
      </div>
      <p className="text-sm text-stone-400 mb-4">
        Connect Cal.com so the assistant can check your availability and book the meeting live
        during a call. Connecting Cal.com replaces Google Calendar as this account's calendar.
      </p>

      {notReady && (
        <p className="mb-4 rounded-lg border border-amber-800/50 bg-amber-950/30 px-4 py-2 text-sm text-amber-200">
          Cal.com connection is being set up on the backend — this will go live shortly.
        </p>
      )}
      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : notReady ? null : !isAdmin ? (
        <p className="text-sm text-stone-500">
          {state.connected
            ? <>Connected
                {state.lengthMin
                  ? <> — Alfred books <span className="text-stone-300">{state.lengthMin}-minute</span> meetings.</>
                  : '.'}</>
            : 'Not connected. Ask an account admin to connect Cal.com.'}
        </p>
      ) : (
        <div className="space-y-3 max-w-xl">
          {state.connected && (
            <p className="text-sm text-stone-300">
              Connected{state.username ? <> as <span className="text-stone-100">{state.username}</span></> : ''}
              {state.eventTypeId ? <> · event type <span className="text-stone-100">{state.eventTypeId}</span></> : ''}.
              {' '}Re-enter your key below to update.
            </p>
          )}
          {/* Duration, when we know it. lengthMin is null for a row connected by raw
              eventTypeId — only the booking-link path looks the event type up. Say
              which case you're in rather than printing nothing, because "no duration
              line" and "books 60-minute meetings" look identical otherwise. */}
          {state.connected && (
            <p className="text-sm text-stone-400">
              {state.lengthMin
                ? <>Alfred books <span className="text-stone-200">{state.lengthMin}-minute</span> meetings
                    {state.lengthOptions?.length > 1
                      ? <> (this event also offers {state.lengthOptions.filter((n) => n !== state.lengthMin).join(', ')} min)</>
                      : ''}.</>
                : <>Meeting length unknown — this account was connected with an event-type ID, which
                    skips the lookup. Re-connect with your booking link to read it.</>}
            </p>
          )}
          <label className="block">
            <span className="field-label">API key</span>
            <input
              type="password"
              className="field-input"
              placeholder={state.connected ? '•••••••• (enter to replace)' : 'cal_live_…'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              autoComplete="off"
            />
            <span className="text-xs text-stone-500 mt-1 block">
              Cal.com → Settings → Developer → API keys. Stored encrypted; never shown again.
            </span>
          </label>
          <label className="block">
            <span className="field-label">Your booking link</span>
            <input
              className="field-input"
              placeholder="https://cal.com/alfredvoice/alfred-voice-demonstration"
              value={bookingUrl}
              onChange={(e) => setBookingUrl(e.target.value)}
            />
            <span className="text-xs text-stone-500 mt-1 block">
              The link you already send people. Paste it exactly — we read the event from it, including
              how long the meeting is. A team link works too.
            </span>
          </label>

          <label className="block">
            <span className="field-label">Username (optional)</span>
            <input
              className="field-input"
              placeholder="e.g. alfredvoice"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <span className="text-xs text-stone-500 mt-1 block">
              Read from the link when you leave this blank. Shown on this card for reference.
            </span>
          </label>

          {/* The numeric id is a fallback, not the ask. It bypasses the resolver —
              which is also what discovers the event's length — so it is deliberately
              behind a disclosure rather than sitting next to the link as an equal. */}
          <div>
            <button
              type="button"
              className="text-xs text-stone-400 hover:text-alfred-gold"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? 'Hide' : 'I have the numeric event-type ID instead'}
            </button>
            {showAdvanced && (
              <label className="block mt-2">
                <span className="field-label">Event type ID</span>
                <input
                  className="field-input"
                  placeholder="e.g. 5523764"
                  value={eventTypeId}
                  onChange={(e) => setEventTypeId(e.target.value)}
                />
                <span className="text-xs text-stone-500 mt-1 block">
                  Only if you already know it. Cal.com doesn&rsquo;t show this anywhere in its
                  interface, so the booking link above is the easier route — and it lets us read the
                  meeting length, which this field does not.
                </span>
              </label>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button type="button" className="btn-primary" onClick={save} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving…' : state.connected ? 'Update' : 'Connect Cal.com'}
            </button>
            {state.connected && (
              <button type="button" className="btn-ghost text-red-300 hover:text-red-200" onClick={disconnect}>
                Disconnect
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
